import { useState, useRef, useEffect, useMemo } from 'react';
import type { FC, HTMLAttributes, CSSProperties } from 'react';

interface TransitionProps extends HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
  duration?: number;
  cache?: boolean;
  getHeight?: (height: string | number) => void;
  onTransitionEnd?: () => void;
}
const Transition: FC<TransitionProps> = ({ children, isOpen = false, duration = 200, cache = false, getHeight, onTransitionEnd, ...otherProps }) => {

  const [height, setHeight] = useState<string | number>(isOpen ? 'auto' : 0);
  const [opacity, setOpacity] = useState<number>(isOpen ? 1 : 0);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentHeight = contentRef.current?.scrollHeight;
    if (contentHeight) {
      setHeight(isOpen ? `${contentHeight + 2}px` : 0);
      setOpacity(isOpen ? 1 : 0);
    }
    if (isOpen && isClosing) {
      setIsClosing(false);
    }
  }, [isOpen, isClosing]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      const contentHeight = entries[0].target.scrollHeight;
      setHeight(isOpen ? `${contentHeight + 2}px` : 0);
    });
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => {
      if (contentRef.current) {
        resizeObserver.unobserve(contentRef.current);
      }
    };
  }, [isOpen, isClosing]);

  const transitionStyle: CSSProperties = useMemo(() => {
    return {
      height,
      opacity,
      overflow: 'hidden',
      transition: `height ${duration}ms ease-in-out, opacity ${duration * 2}ms ease-in-out`
    }
  }, [height, duration])
  
  const handleTransitionEnd = () => {
    if (!isOpen && !cache) {
      setIsClosing(true);
    }
    getHeight?.(height);
    onTransitionEnd?.();
  };

  if (isClosing) {
    return null;
  }

  return (
    <div style={transitionStyle} {...otherProps} onTransitionEnd={handleTransitionEnd}>
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};
export default Transition;